import Svg, { Circle, G, Mask, Path, Rect, Text as SvgText } from "react-native-svg";

import {
  AUDIENCE_ICON_PATHS,
  AUDIENCE_ICON_TRANSFORM,
  CLEAN_AUDIO_ICON_BAR_RADIUS,
  CLEAN_AUDIO_ICON_BARS
} from "@/design/audioTrackIconGeometry";
import { palette } from "@/design/tokens";

/** Crisp 24×24 icon set — filled, rounded, quiet. */

type IconProps = {
  size?: number;
  color?: string;
};

function makeIcon(paths: string[]) {
  return function Icon({ size = 20, color = palette.text }: IconProps) {
    return (
      <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        {paths.map((d, index) => (
          <Path key={index} d={d} fill={color} fillRule="evenodd" />
        ))}
      </Svg>
    );
  };
}

export const PlayIcon = makeIcon(["M8.5 5.5a1 1 0 0 1 1.53-.85l10 6.5a1 1 0 0 1 0 1.7l-10 6.5a1 1 0 0 1-1.53-.85V5.5z"]);

/** Play-from-the-top: leading bar + play triangle. */
export const PlayFromStartIcon = makeIcon([
  "M5.5 5A1.5 1.5 0 0 1 7 6.5v11a1.5 1.5 0 0 1-3 0v-11A1.5 1.5 0 0 1 5.5 5z",
  "M9.5 6.6a1 1 0 0 1 1.53-.85l8.3 5.4a1 1 0 0 1 0 1.7l-8.3 5.4a1 1 0 0 1-1.53-.85V6.6z"
]);

export const PauseIcon = makeIcon([
  "M7 5a1.5 1.5 0 0 1 1.5 1.5v11a1.5 1.5 0 0 1-3 0v-11A1.5 1.5 0 0 1 7 5z",
  "M17 5a1.5 1.5 0 0 1 1.5 1.5v11a1.5 1.5 0 0 1-3 0v-11A1.5 1.5 0 0 1 17 5z"
]);

export const NextIcon = makeIcon([
  "M6 6.6a1 1 0 0 1 1.55-.83l7.5 5.4a1 1 0 0 1 0 1.66l-7.5 5.4A1 1 0 0 1 6 17.4V6.6z",
  "M17 5.5a1.25 1.25 0 0 1 2.5 0v13a1.25 1.25 0 0 1-2.5 0v-13z"
]);

export const PreviousIcon = makeIcon([
  "M18 6.6a1 1 0 0 0-1.55-.83l-7.5 5.4a1 1 0 0 0 0 1.66l7.5 5.4A1 1 0 0 0 18 17.4V6.6z",
  "M7 5.5a1.25 1.25 0 0 0-2.5 0v13a1.25 1.25 0 0 0 2.5 0v-13z"
]);

export const FullscreenIcon = makeIcon([
  "M4 9a1 1 0 0 0 2 0V6h3a1 1 0 0 0 0-2H5a1 1 0 0 0-1 1v4z",
  "M20 9a1 1 0 0 1-2 0V6h-3a1 1 0 0 1 0-2h4a1 1 0 0 1 1 1v4z",
  "M4 15a1 1 0 0 1 2 0v3h3a1 1 0 0 1 0 2H5a1 1 0 0 1-1-1v-4z",
  "M20 15a1 1 0 0 0-2 0v3h-3a1 1 0 0 0 0 2h4a1 1 0 0 0 1-1v-4z"
]);

/** Inward mirror of FullscreenIcon — brackets collapse toward center. */
export const FullscreenExitIcon = makeIcon([
  "M9 4a1 1 0 0 0-2 0v3H4a1 1 0 0 0 0 2h4a1 1 0 0 0 1-1V4z",
  "M15 4a1 1 0 0 1 2 0v3h3a1 1 0 0 1 0 2h-4a1 1 0 0 1-1-1V4z",
  "M9 20a1 1 0 0 1-2 0v-3H4a1 1 0 0 1 0-2h4a1 1 0 0 1 1 1v4z",
  "M15 20a1 1 0 0 0 2 0v-3h3a1 1 0 0 0 0-2h-4a1 1 0 0 0-1 1v4z"
]);

/** Expand the native video inside an already-fullscreen player canvas. */
export function VideoFillIcon({ size = 20, color = palette.text }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x={3} y={5} width={18} height={14} rx={2} stroke={color} strokeWidth={2} />
      <Path
        d="M10 11 7 8m0 0h3M7 8v3m7 2 3 3m0 0h-3m3 0v-3"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </Svg>
  );
}

/** Restore the complete native video with centered pillar/letterboxing. */
export function VideoFitIcon({ size = 20, color = palette.text }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect
        x={3}
        y={5}
        width={18}
        height={14}
        rx={2}
        stroke={color}
        strokeOpacity={0.55}
        strokeWidth={2}
      />
      <Rect x={6.5} y={5} width={11} height={14} rx={1.5} stroke={color} strokeWidth={2} />
    </Svg>
  );
}

/** Native rendering of @videojs/react's minimal CastEnterIcon geometry. */
export function CastIcon({ size = 20, color = palette.text }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path
        d="M15.154 2.004A3 3 0 0 1 18 5v8l-.004.154a3 3 0 0 1-2.842 2.842L15 16H7.5q-.002-.772-.15-1.5H15a1.5 1.5 0 0 0 1.5-1.5V5A1.5 1.5 0 0 0 15 3.5H3A1.5 1.5 0 0 0 1.5 5v3.65A7.5 7.5 0 0 0 0 8.5V5a3 3 0 0 1 3-3h12z"
        fill={color}
      />
      <Mask id="cast-waves" x={0} y={8} width={8} height={8} maskUnits="userSpaceOnUse">
        <Rect x={0} y={8} width={8} height={8} rx={0.5} fill="#fff" />
      </Mask>
      <G mask="url(#cast-waves)">
        <Circle cx={0} cy={16} r={3.25} stroke={color} strokeWidth={1.5} />
        <Circle cx={0} cy={16} r={5.75} stroke={color} strokeWidth={1.5} />
        <Circle cx={0} cy={16} r={1.5} fill={color} />
      </G>
    </Svg>
  );
}

export const AirPlayIcon = makeIcon([
  "M5 4h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-2.1l-1.5-2H19a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3.6l-1.5 2H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3z",
  "M11.2 13.4a1 1 0 0 1 1.6 0l4.8 6.4a1 1 0 0 1-.8 1.6H7.2a1 1 0 0 1-.8-1.6l4.8-6.4z"
]);

/** Native rendering of @videojs/react's minimal PipEnterIcon geometry. */
export function PictureInPictureIcon({ size = 20, color = palette.text }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill={color}>
      <Path d="M13 2a4 4 0 0 1 4 4v2.645a3.5 3.5 0 0 0-1-.145h-.5V6A2.5 2.5 0 0 0 13 3.5H4A2.5 2.5 0 0 0 1.5 6v6A2.5 2.5 0 0 0 4 14.5h2.5v.5c0 .347.05.683.145 1H4a4 4 0 0 1-4-4V6a4 4 0 0 1 4-4z" />
      <Rect width={10} height={7} x={8} y={10} rx={2} />
      <Path d="M7.25 10A.75.75 0 0 0 8 9.25v-3.5a.75.75 0 0 0-1.5 0V8.5H3.75a.75.75 0 0 0-.743.648L3 9.25c0 .414.336.75.75.75z" />
      <Path d="M6.72 9.78a.75.75 0 0 0 1.06-1.06l-3.5-3.5a.75.75 0 0 0-1.06 1.06z" />
    </Svg>
  );
}

/** Native render of @videojs/react's default VolumeHighIcon geometry. */
export function VolumeIcon({ size = 20, color = palette.text }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill={color}>
      <Path d="M15.6 3.3c-.4-.4-1-.4-1.4 0s-.4 1 0 1.4C15.4 5.9 16 7.4 16 9s-.6 3.1-1.8 4.3c-.4.4-.4 1 0 1.4.2.2.5.3.7.3.3 0 .5-.1.7-.3C17.1 13.2 18 11.2 18 9s-.9-4.2-2.4-5.7" />
      <Path d="M.714 6.008h3.072l4.071-3.857c.5-.376 1.143 0 1.143.601V15.28c0 .602-.643.903-1.143.602l-4.071-3.858H.714c-.428 0-.714-.3-.714-.752V6.76c0-.451.286-.752.714-.752m10.568.59a.91.91 0 0 1 0-1.316.91.91 0 0 1 1.316 0c1.203 1.203 1.47 2.216 1.522 3.208q.012.255.011.51c0 1.16-.358 2.733-1.533 3.803a.7.7 0 0 1-.298.156c-.382.106-.873-.011-1.018-.156a.91.91 0 0 1 0-1.316c.57-.57.995-1.551.995-2.487 0-.944-.26-1.667-.995-2.402" />
    </Svg>
  );
}

/** Native render of @videojs/react's default VolumeOffIcon geometry. */
export function MutedIcon({ size = 20, color = palette.text }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill={color}>
      <Path d="M.714 6.008h3.072l4.071-3.857c.5-.376 1.143 0 1.143.601V15.28c0 .602-.643.903-1.143.602l-4.071-3.858H.714c-.428 0-.714-.3-.714-.752V6.76c0-.451.286-.752.714-.752M14.5 7.586l-1.768-1.768a1 1 0 1 0-1.414 1.414L13.085 9l-1.767 1.768a1 1 0 0 0 1.414 1.414l1.768-1.768 1.768 1.768a1 1 0 0 0 1.414-1.414L15.914 9l1.768-1.768a1 1 0 0 0-1.414-1.414z" />
    </Svg>
  );
}

/** Audience mix — normalized from the supplied three-person SVG. */
export function AudienceIcon({ size = 20, color = palette.text }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <G transform={AUDIENCE_ICON_TRANSFORM}>
        {AUDIENCE_ICON_PATHS.map((d) => (
          <Path key={d} d={d} />
        ))}
      </G>
    </Svg>
  );
}

/** Clean feed — supplied waveform normalized to the app's optical weight. */
export function CleanAudioIcon({ size = 20, color = palette.text }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      {CLEAN_AUDIO_ICON_BARS.map((bar) => (
        <Rect key={bar.x} {...bar} rx={CLEAN_AUDIO_ICON_BAR_RADIUS} fill={color} />
      ))}
    </Svg>
  );
}

function VideoJsSeekIcon({
  backwards,
  color = palette.text,
  size = 20
}: IconProps & { backwards: boolean }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill={color}>
      <G transform={backwards ? "translate(18 0) scale(-1 1)" : undefined}>
        <Path d="M9 1a7.98 7.98 0 0 1 6.132 2.867l1.441-1.44a.25.25 0 0 1 .427.177V6.75a.25.25 0 0 1-.25.25h-4.146a.25.25 0 0 1-.177-.427L13.71 5.29A5.99 5.99 0 0 0 9 3a6 6 0 0 0-4.242 10.242l-1.415 1.415A8 8 0 0 1 9 1" />
      </G>
      <SvgText
        fill={color}
        fontSize={6.5}
        fontWeight="600"
        textAnchor={backwards ? "start" : "end"}
        x={backwards ? 1 : 17}
        y={17}
      >
        10
      </SvgText>
    </Svg>
  );
}

/** Native render of @videojs/react's SeekIcon, mirrored for backward seeking. */
export function SeekBackwardIcon(props: IconProps) {
  return <VideoJsSeekIcon {...props} backwards />;
}

/** Native render of @videojs/react's SeekIcon. */
export function SeekForwardIcon(props: IconProps) {
  return <VideoJsSeekIcon {...props} backwards={false} />;
}

export const ChevronRightIcon = makeIcon([
  "M9.3 5.3a1 1 0 0 1 1.4 0l6 6a1 1 0 0 1 0 1.4l-6 6a1 1 0 0 1-1.4-1.4L14.6 12 9.3 6.7a1 1 0 0 1 0-1.4z"
]);

export const CheckIcon = makeIcon([
  "M20.3 6.3a1 1 0 0 1 0 1.4l-9.5 9.5a1 1 0 0 1-1.4 0l-4.7-4.7a1 1 0 1 1 1.4-1.4l4 4 8.8-8.8a1 1 0 0 1 1.4 0z"
]);

/** Exact native rendering of @videojs/react's minimal GearIcon geometry. */
export function GearIcon({ size = 20, color = palette.text }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill={color}>
      <Path d="M7.519 2.184c.356-1.579 2.605-1.579 2.962 0a1.52 1.52 0 0 0 2.292.949c1.368-.864 2.958.727 2.094 2.095-.56.886-.073 2.06.95 2.29 1.578.357 1.578 2.607 0 2.964a1.518 1.518 0 0 0-.95 2.29c.864 1.369-.726 2.96-2.094 2.095a1.52 1.52 0 0 0-2.292.95c-.357 1.578-2.606 1.578-2.962 0a1.518 1.518 0 0 0-2.291-.95c-1.368.864-2.959-.726-2.095-2.094.56-.887.074-2.06-.95-2.291-1.578-.357-1.578-2.607 0-2.963a1.518 1.518 0 0 0 .95-2.291c-.864-1.368.727-2.959 2.095-2.095.886.56 2.06.074 2.29-.95M9 5.1a3.9 3.9 0 1 0 0 7.8 3.9 3.9 0 0 0 0-7.8" />
    </Svg>
  );
}

export const DownloadIcon = makeIcon([
  "M12 3a1 1 0 0 1 1 1v8.6l2.3-2.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L11 12.6V4a1 1 0 0 1 1-1z",
  "M4 16a1 1 0 0 1 1 1v1.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V17a1 1 0 1 1 2 0v1.5A3.5 3.5 0 0 1 17.5 22h-11A3.5 3.5 0 0 1 3 18.5V17a1 1 0 0 1 1-1z"
]);

export const RenewIcon = makeIcon([
  "M12 4a8 8 0 0 1 7.4 5H21a1 1 0 0 1 .8 1.6l-2.5 3.3a1 1 0 0 1-1.6 0l-2.5-3.3A1 1 0 0 1 16 9h1.3A6 6 0 0 0 6.7 8.2a1 1 0 0 1-1.66-1.12A8 8 0 0 1 12 4z",
  "M5.3 10.1a1 1 0 0 1 .8.4l2.5 3.3A1 1 0 0 1 7.8 15H6.7a6 6 0 0 0 10.6.8 1 1 0 0 1 1.66 1.12A8 8 0 0 1 4.6 15H3a1 1 0 0 1-.8-1.6l2.5-3.3a1 1 0 0 1 .8-.4z"
]);

export const UserIcon = makeIcon([
  "M12 3a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9z",
  "M4 19.2C4 15.9 7.6 14 12 14s8 1.9 8 5.2c0 1-.8 1.8-1.8 1.8H5.8C4.8 21 4 20.2 4 19.2z"
]);

export const SearchIcon = makeIcon([
  "M10.5 3a7.5 7.5 0 1 0 4.55 13.46l4.24 4.25a1.1 1.1 0 0 0 1.56-1.56l-4.25-4.24A7.5 7.5 0 0 0 10.5 3zm-5.3 7.5a5.3 5.3 0 1 1 10.6 0 5.3 5.3 0 0 1-10.6 0z"
]);

export const MonitorIcon = makeIcon([
  "M5.5 4A2.5 2.5 0 0 0 3 6.5v7A2.5 2.5 0 0 0 5.5 16h13a2.5 2.5 0 0 0 2.5-2.5v-7A2.5 2.5 0 0 0 18.5 4h-13z",
  "M12 17.5a1 1 0 0 1 1 1V19h2.5a1 1 0 1 1 0 2h-7a1 1 0 1 1 0-2H11v-.5a1 1 0 0 1 1-1z"
]);

export const PhoneIcon = makeIcon([
  "M8.5 2A2.5 2.5 0 0 0 6 4.5v15A2.5 2.5 0 0 0 8.5 22h7a2.5 2.5 0 0 0 2.5-2.5v-15A2.5 2.5 0 0 0 15.5 2h-7zm2 15.5h3a1 1 0 1 1 0 2h-3a1 1 0 1 1 0-2z"
]);

export const TabletIcon = makeIcon([
  "M6.5 2A2.5 2.5 0 0 0 4 4.5v15A2.5 2.5 0 0 0 6.5 22h11a2.5 2.5 0 0 0 2.5-2.5v-15A2.5 2.5 0 0 0 17.5 2h-11zm4 16.5h3a1 1 0 1 1 0 2h-3a1 1 0 1 1 0-2z"
]);

export const HeartIcon = makeIcon([
  "M12 20.7l-1.2-1C5.9 15.4 3 12.8 3 9.6 3 7 5 5 7.6 5c1.5 0 2.9.7 3.9 1.8.4.5 1.6.5 2 0C14.5 5.7 15.9 5 17.4 5 20 5 22 7 22 9.6c0 3.2-2.9 5.8-7.8 10.1l-1.2 1a1.5 1.5 0 0 1-2 0z"
]);
